import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Persisted view-mode preference for the Projects list (CLAUDE.md §2.3),
 * mirroring the CRM list stores. Search/status/company filters live in
 * page-local state and stay out of the store — those are server-driven query
 * params, not durable user prefs.
 */
export type ProjectsViewMode = 'table' | 'list' | 'cards' | 'grouped';

export const PROJECTS_VIEW_MODES: ProjectsViewMode[] = [
  'table',
  'list',
  'cards',
  'grouped',
];

interface ProjectsUiState {
  view: ProjectsViewMode;
  setView: (view: ProjectsViewMode) => void;
  /** Current DRF `OrderingFilter` value (e.g. `-created`). Session-only. */
  ordering: string | undefined;
  setOrdering: (ordering: string | undefined) => void;
  /** `all` | a ProjectStatus value. Session-only. */
  status: string;
  setStatus: (status: string) => void;
  /** `all` | `public` | `private`. Session-only. */
  visibility: string;
  setVisibility: (visibility: string) => void;
  /** Show archived projects instead of active ones. Session-only. */
  showArchived: boolean;
  setShowArchived: (showArchived: boolean) => void;
}

export const useProjectsUi = create<ProjectsUiState>()(
  persist(
    (set) => ({
      view: 'cards',
      setView: (view) => set({ view }),
      ordering: undefined,
      setOrdering: (ordering) => set({ ordering }),
      status: 'all',
      setStatus: (status) => set({ status }),
      visibility: 'all',
      setVisibility: (visibility) => set({ visibility }),
      showArchived: false,
      setShowArchived: (showArchived) => set({ showArchived }),
    }),
    {
      name: 'projects:ui',
      // Only the view mode is durable — ordering stays ephemeral so refreshes
      // return to a clean default.
      partialize: (state) => ({ view: state.view }),
    },
  ),
);
